import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type FocusEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { colors } from 'src/theme';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

const ARROW = 6;
const GAP = 8;
const HIDE_DELAY_MS = 120;
const VIEWPORT_PAD = 8;
/** Narrow layout: nudge top/bottom tooltips left (fixed + innerWidth clamp often reads slightly wide on mobile). */
const MOBILE_MAX_WIDTH_PX = 576;
const MOBILE_TOP_BOTTOM_LEFT_NUDGE_PX = 10;

const arrowStyles = (p: TooltipPlacement) => {
  const base = {
    content: '""',
    position: 'absolute' as const,
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
  };
  switch (p) {
    case 'bottom':
      return {
        ...base,
        top: 0,
        left: '50%',
        transform: 'translate(-50%, -100%)',
        borderWidth: `0 ${ARROW}px ${ARROW}px ${ARROW}px`,
        borderBottomColor: colors.background7,
      };
    case 'left':
      return {
        ...base,
        right: 0,
        top: '50%',
        transform: 'translate(100%, -50%)',
        borderWidth: `${ARROW}px 0 ${ARROW}px ${ARROW}px`,
        borderLeftColor: colors.background7,
      };
    case 'right':
      return {
        ...base,
        left: 0,
        top: '50%',
        transform: 'translate(-100%, -50%)',
        borderWidth: `${ARROW}px ${ARROW}px ${ARROW}px 0`,
        borderRightColor: colors.background7,
      };
    case 'top':
    default:
      return {
        ...base,
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        borderWidth: `${ARROW}px ${ARROW}px 0 ${ARROW}px`,
        borderTopColor: colors.background7,
      };
  }
};

const Root = styled.div({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  maxWidth: '100%',
});

const Panel = styled.div<{
  $placement: TooltipPlacement;
  $open: boolean;
  $maxWidth: number | string;
}>(({ $placement, $open, $maxWidth }) => ({
  position: 'fixed',
  zIndex: 2147483646,
  padding: '8px 12px',
  borderRadius: '6px',
  backgroundColor: colors.background7,
  color: colors.text1,
  fontSize: '12px',
  lineHeight: '16px',
  fontFamily: "'Red Hat Text', sans-serif",
  maxWidth: $maxWidth,
  boxSizing: 'border-box',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  opacity: $open ? 1 : 0,
  visibility: $open ? ('visible' as const) : ('hidden' as const),
  pointerEvents: $open ? ('auto' as const) : ('none' as const),
  transition: 'opacity 0.15s ease, visibility 0.15s ease',
  textAlign: 'left',
  '&::after': arrowStyles($placement),
}));

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  placement?: TooltipPlacement;
  /** Max width of the tooltip panel (default 280px). */
  maxWidth?: number | string;
  className?: string;
};

function mergeDescribedBy(existing: string | undefined, tooltipId: string) {
  return existing ? `${existing} ${tooltipId}` : tooltipId;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}

function getViewportClampSize() {
  const el = document.documentElement;
  return {
    width: el.clientWidth || window.innerWidth,
    height: el.clientHeight || window.innerHeight,
  };
}

export const Tooltip: FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  maxWidth = 280,
  className,
}) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    cancelHide();
    setOpen(true);
  }, [cancelHide]);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      setOpen(false);
    }, HIDE_DELAY_MS);
  }, [cancelHide]);

  const updatePosition = useCallback(() => {
    const rootEl = rootRef.current;
    const panelEl = panelRef.current;
    if (!open || !rootEl || !panelEl) return;

    const tr = rootEl.getBoundingClientRect();
    const pr = panelEl.getBoundingClientRect();
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'bottom':
        top = tr.bottom + GAP;
        left = tr.left + tr.width / 2 - pr.width / 2;
        break;
      case 'left':
        top = tr.top + tr.height / 2 - pr.height / 2;
        left = tr.left - pr.width - GAP;
        break;
      case 'right':
        top = tr.top + tr.height / 2 - pr.height / 2;
        left = tr.right + GAP;
        break;
      case 'top':
      default:
        top = tr.top - pr.height - GAP;
        left = tr.left + tr.width / 2 - pr.width / 2;
        break;
    }

    const { width: layoutW, height: layoutH } = getViewportClampSize();
    const isNarrow = layoutW <= MOBILE_MAX_WIDTH_PX;
    if (isNarrow && (placement === 'top' || placement === 'bottom')) {
      left -= MOBILE_TOP_BOTTOM_LEFT_NUDGE_PX;
    }

    left = clamp(left, VIEWPORT_PAD, layoutW - pr.width - VIEWPORT_PAD);
    top = clamp(top, VIEWPORT_PAD, layoutH - pr.height - VIEWPORT_PAD);
    setCoords({ top, left });
  }, [open, placement]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updatePosition();
  }, [open, placement, maxWidth, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onScrollOrResize);
    vv?.addEventListener('scroll', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      vv?.removeEventListener('resize', onScrollOrResize);
      vv?.removeEventListener('scroll', onScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(() => () => cancelHide(), [cancelHide]);

  const handleBlur = (e: FocusEvent<HTMLElement>) => {
    const next = e.relatedTarget as Node | null;
    if (
      next &&
      (rootRef.current?.contains(next) || panelRef.current?.contains(next))
    ) {
      return;
    }
    cancelHide();
    setOpen(false);
  };

  const trigger: ReactNode =
    isValidElement(children)
      ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, {
          'aria-describedby': mergeDescribedBy(
            (children as ReactElement<{ 'aria-describedby'?: string }>).props['aria-describedby'],
            id,
          ),
        })
      : (
          <span aria-describedby={id} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {children}
          </span>
        );

  const panelNode =
    open &&
    typeof document !== 'undefined' && (
      <Panel
        ref={panelRef}
        id={id}
        role="tooltip"
        aria-hidden={false}
        $placement={placement}
        $open={open}
        $maxWidth={maxWidth}
        style={{
          top: coords?.top ?? 0,
          left: coords?.left ?? 0,
        }}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onBlur={handleBlur}
      >
        {content}
      </Panel>
    );

  return (
    <Root
      ref={rootRef}
      className={className}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      onFocus={show}
      onBlur={handleBlur}
    >
      {trigger}
      {panelNode && createPortal(panelNode, document.body)}
    </Root>
  );
};
